<template>
  <b-row>
    <b-col cols="12" md="4">
      <v-select
        v-model="filter.field"
        :options="supportedFields"
        text-field="label"
        size="small"
        :reduce="(val) => val.value"
      />
    </b-col>

    <b-col cols="12" md="4">
      <v-select
        v-model="filter.operator"
        :options="supportedOperations"
        text-field="label"
        size="small"
        :reduce="(val) => val.value"
      />
    </b-col>

    <b-col v-if="selectedField" cols="12" md="4">
      <div class="d-flex align-items-center">
        <div class="flex-grow-1">
          <b-form-input
            v-if="selectedField.type === 'text'"
            v-model="filter.value"
            size="sm"
            class="mr-1"
            style="height: 32px;"
          />
          <b-form-input
            v-if="selectedField.type === 'number'"
            v-model="filter.value"
            size="sm"
            type="number"
            class="mr-1"
            style="height: 33px;"
          />
          <v-select
            v-if="selectedField.type === 'select'"
            v-model="filter.value"
            :options="selectedField.selectOptions"
            text-field="label"
            size="small"
            :multiple="canMultiSelect"
            :reduce="(val) => val.value"
          />
          <geo-selector
            v-if="selectedField.type === 'selector'"
            v-model="filter.value"
            :selector-type="selectedField.selectorType"
            :geo-filter-values="geoFilterValues"
            :multiple="canMultiSelect"
          />
        </div>
        <b-button
          size="sm"
          variant="outline-info"
          class="ml-2"
          @click="$emit('remove')"
        >
          <feather-icon icon="XIcon" color="gray" />
        </b-button>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import vSelect from "vue-select";

import GeoSelector from "@core/components/query-builder/geo-selector.vue";
import { BButton, BFormInput, BFormSelect, BCol, BRow } from "bootstrap-vue";

export default {
  name: "SearchFilterRow",
  components: {
    vSelect,
    BButton,
    BFormInput,
    BFormSelect,
    GeoSelector,
    BCol,
    BRow,
  },
  props: {
    filter: {
      type: Object,
      default: () => {},
    },
    regionOptions: {
      type: Array,
      default: () => [],
    },
    selectedFields: {
      type: Array,
      default: () => [],
    },
    memberStatusOptions: {
      type: Array,
      default: () => [],
    },
    memberTypeOptions: {
      type: Array,
      default: () => [],
    },
    memberTitleOptions: {
      type: Array,
      default: () => [],
    },
    memberDesignationOptions: {
      type: Array,
      default: () => [],
    },
    geoFilterValues: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      updateKey: 0,
      allOperations: [
        { label: "Select Operator", value: null },
        { label: "Equals", value: "equal_to", fields: "all" },
        { label: "Not Equals", value: "not_equals", fields: "all" },
        { label: "Greater Than", value: "greater_than", fields: ["text", "number"], },
        { label: "Less Than", value: "less_than", fields: ["text", "number"], },
        {
          label: "Less Than Or Equal To",
          value: "less_than_or_equal_to",
          fields: ["text", "number"],
        },
        {
          label: "Greater Than Or Equal To",
          value: "greater_than_or_equal_to",
          fields: ["text", "number"],
        },
        {
          label: "Includes",
          value: "in",
          fields: ["select", "selector"],
          isArray: true,
        },
        {
          label: "Not Includes",
          value: "not_in",
          fields: ["select", "selector"],
          isArray: true,
        },
      ],
    };
  },
  computed: {
    supportedOperations() {
      if (this.selectedField) {
        return this.allOperations.filter((op) => {
          if (op.fields === "all") {
            return true;
          }
          if (Array.isArray(op.fields)) {
            return op.fields.includes(this.selectedField.type);
          }
          return false;
        });
      }
      return this.allOperations
    },
    canMultiSelect() {
      const operator = this.allOperations.find(
        (op) => op.value === this.filter.operator
      );
      if (this.selectedField) {
        return ["selector", "select"].includes(this.selectedField.type) && operator.isArray;
      }
      return false
    },
    selectedField() {
      const selected = this.supportedFields.find(
        (field) => field.value === this.filter.field
      );
      return selected;
    },
    supportedFields() {
      return [
        { label: "Select Field", value: null, type: "" },
        { label: "Age", value: "age", type: "number" },
        { label: "Email", value: "email", type: "text" },
        {
          label: "Member Type",
          value: "member_type_id",
          type: "select",
          selectOptions: this.memberTypeOptions,
        },
        {
          label: "Member Title",
          value: "member_title_id",
          type: "select",
          selectOptions: this.memberTitleOptions,
        },
        {
          label: "Member Designation",
          value: "member_designation_id",
          type: "select",
          selectOptions: this.memberDesignationOptions,
        },
        {
          label: "Status",
          value: "member_status_id",
          type: "select",
          selectOptions: this.memberStatusOptions,
        },
        {
          label: "Region",
          value: "region",
          type: "select",
          selectOptions: this.regionOptions,
        },
        {
          label: "Mission Zone",
          value: "mission_zone",
          type: "select",
          selectOptions: this.yesNoOptions,
        },
        {
          label: "Area",
          value: "area",
          type: "selector",
          selectorType: "area",
        },
        {
          label: "District",
          value: "district",
          type: "selector",
          selectorType: "district",
        },
        {
          label: "Assembly",
          value: "assembly",
          type: "selector",
          selectorType: "assembly",
        },
        {
          label: "In School",
          value: "is_schooling",
          type: "select",
          selectOptions: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        {
          label: "Is Secondment",
          value: "is_secondment",
          type: "select",
          selectOptions: [
            { label: "Yes", value: true },
            { label: "No", value: false },
          ],
        },
        { label: "Retirement Year", value: "retired_year", type: "number" },
        {
          label: "Year called into ministry",
          value: "year_called_into_ministry",
          type: "text",
        },
        { label: "Years Served", value: "years_served", type: "number" },
      ].map((field) => ({
        ...field,
        disabled: this.selectedFields.includes(field.value),
      }));
    },
    availableFields() {
      return this.supportedFields.filter(
        (field) => !this.selectedFields.includes(field.value)
      );
    },
  },
  watch: {
    selectedField: {
      deep: true,
      handler(val) {
        if (val && val.type && this.filter) {
          if (this.filter.type !== val.type) {
            this.filter.type = val.type;
            this.filter.value = null;
          }
        }
      },
    },
  },
};
</script>

<style></style>
